/**
 * Dev Auto-Complete Service
 *
 * Ports the logic from scripts/auto-complete-lessons.ts and
 * scripts/auto-complete-module-end-quiz.ts to work in the browser
 * using the existing frontend API wrappers.
 *
 * DEV ONLY - should only be imported when import.meta.env.DEV is true.
 */

import { learningSessionApi, type ComponentDetail, type ComponentLocation } from '@/api/learningSession'
import { contentApi, type ContentHierarchyNode } from '@/api/content'
import { quizScriptApi } from '@/api/quizScript'

// ============================================================================
// Constants
// ============================================================================

const VIDEO_HEARTBEAT_INTERVAL_SEC = 5
const VIDEO_SIMULATE_SPEED_X = 20
const DELAY_BETWEEN_COMPONENTS_MS = 800
const DELAY_BETWEEN_LESSONS_MS = 1500
const REQUIRED_WATCH_PERCENT = 90

// ============================================================================
// Types
// ============================================================================

export type LogLevel = 'info' | 'success' | 'warn' | 'error'

export interface AutoCompleteCallbacks {
    onLog: (message: string, level: LogLevel) => void
    onProgress: (current: number, total: number, label: string) => void
    onComplete: () => void
    signal?: AbortSignal
}

interface ClassCourseParams {
    classProductId: number
    productId: number
    classId: number
}

// ============================================================================
// Helpers
// ============================================================================

function sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
}

function checkAbort(signal?: AbortSignal): void {
    if (signal?.aborted) {
        throw new DOMException('Auto-complete cancelled', 'AbortError')
    }
}

function toComponentLocation(type: string, location: string): ComponentLocation {
    if (type === 'video') return 'video'
    if (type === 'assignment') return 'assignment'

    switch (location) {
        case 'pre': return 'pre_quiz'
        case 'post': return 'post_quiz'
        case 'wrapup': return 'wrapup_quiz'
        case 'checkup': return 'checkup_quiz'
        default: return 'post_quiz'
    }
}

function getDummyAnswers(quizType: string): unknown[] {
    switch (quizType) {
        case 'multi_choice': return [1]
        case 'boolean': return [true]
        case 'fill_blank':
        case 'short_answer': return ['dummy']
        case 'select_text': return [0]
        case 'drag_text': return ['dummy']
        default: return [1]
    }
}

function collectLessons(node: ContentHierarchyNode): { id: number; title: string }[] {
    if (node.type === 'lesson') {
        return [{ id: node.id, title: node.bareTitle || node.title }]
    }
    return node.children.flatMap(collectLessons)
}

// ============================================================================
// Video Auto-Complete
// ============================================================================

async function autoCompleteVideo(
    componentProgressId: number,
    duration: number,
    classProductId: number,
    cb: AutoCompleteCallbacks,
): Promise<void> {
    const totalDuration = duration || 300
    const simulatedStep = VIDEO_HEARTBEAT_INTERVAL_SEC * VIDEO_SIMULATE_SPEED_X

    cb.onLog(`영상 시뮬레이션 시작 (길이: ${totalDuration}초, ${VIDEO_SIMULATE_SPEED_X}배속)`, 'info')

    // Play event
    await learningSessionApi.recordVideoEvent(componentProgressId, {
        eventType: 'play',
        startPositionSec: 0,
        totalDurationSec: totalDuration,
        requiredWatchPercent: REQUIRED_WATCH_PERCENT,
        classProductId,
    })

    // Heartbeats until progress >= requiredWatchPercent
    let currentPos = 0
    while (currentPos < totalDuration) {
        checkAbort(cb.signal)
        currentPos = Math.min(currentPos + simulatedStep, totalDuration)

        const heartbeat = await learningSessionApi.recordVideoHeartbeat(componentProgressId, {
            currentPositionSec: currentPos,
            watchedDurationSec: simulatedStep,
            totalDurationSec: totalDuration,
            requiredWatchPercent: REQUIRED_WATCH_PERCENT,
            classProductId,
        })

        const pct = heartbeat.progressPercent
        cb.onLog(`영상 진행: ${pct}% (${Math.floor(currentPos)}/${totalDuration}초)`, 'info')

        if (heartbeat.isCompleted || pct >= REQUIRED_WATCH_PERCENT) {
            break
        }
    }

    // End event
    await learningSessionApi.recordVideoEvent(componentProgressId, {
        eventType: 'end',
        startPositionSec: 0,
        endPositionSec: totalDuration,
        totalDurationSec: totalDuration,
        requiredWatchPercent: REQUIRED_WATCH_PERCENT,
        classProductId,
    })

    // Complete
    await learningSessionApi.completeSession(componentProgressId, {
        componentType: 'video',
        forceComplete: true,
        classProductId,
    })

    cb.onLog('영상 완료', 'success')
}

// ============================================================================
// Quiz Auto-Complete
// ============================================================================

async function autoCompleteQuiz(
    sessionId: number,
    componentProgressId: number,
    quizzes: { quizId: string; scriptId: string | null; type: string }[],
    classProductId: number,
    cb: AutoCompleteCallbacks,
): Promise<void> {
    cb.onLog(`퀴즈 ${quizzes.length}문제 풀기`, 'info')

    for (let i = 0; i < quizzes.length; i++) {
        checkAbort(cb.signal)
        const quiz = quizzes[i]

        // Get vars for variable quizzes
        const quizDetail = await quizScriptApi.getQuiz(quiz.quizId)
        const vars = (quizDetail?.vars as Record<string, unknown>) || {}
        const basePayload = { quizId: quiz.quizId, scriptId: quiz.scriptId, vars, timeSpentSec: 3 }

        // 1st: dummy answer to get correctAnswers
        const firstResult = await learningSessionApi.submitQuizResponse(sessionId, {
            ...basePayload,
            answers: getDummyAnswers(quiz.type),
        })

        if (firstResult.isCorrect) {
            cb.onLog(`  문제 ${i + 1}/${quizzes.length}: 정답 (${quiz.type})`, 'info')
            continue
        }

        if (!firstResult.correctAnswers || firstResult.correctAnswers.length === 0) {
            cb.onLog(`  문제 ${i + 1}/${quizzes.length}: correctAnswers 없음 (${quiz.type})`, 'warn')
            continue
        }

        // 2nd: resubmit with correct answers
        const secondResult = await learningSessionApi.submitQuizResponse(sessionId, {
            ...basePayload,
            answers: firstResult.correctAnswers,
        })

        cb.onLog(
            `  문제 ${i + 1}/${quizzes.length}: ${secondResult.isCorrect ? '정답' : '오답'} (${quiz.type})`,
            secondResult.isCorrect ? 'info' : 'warn',
        )
    }

    // Complete quiz session
    const completion = await learningSessionApi.completeSession(componentProgressId, {
        componentType: 'quiz',
        forceComplete: false,
        classProductId,
        passScore: 0,
    })

    cb.onLog(`퀴즈 완료 (점수: ${completion.score ?? '-'}점)`, 'success')
}

// ============================================================================
// Diary (Metacognition) Auto-Complete
// ============================================================================

async function autoCompleteDiary(
    componentProgressId: number,
    quizzes: { quizId: string; scriptId: string | null; type: string }[],
    classProductId: number,
    cb: AutoCompleteCallbacks,
): Promise<void> {
    cb.onLog(`메타인지 ${quizzes.length}문제 답변`, 'info')

    const responses = quizzes.map(quiz => ({
        quizId: quiz.quizId,
        scriptId: quiz.scriptId,
        answers: ['자동 완료 도구에 의해 생성된 답변입니다.'] as unknown[],
        vars: {} as Record<string, unknown>,
    }))

    await learningSessionApi.submitDiaryQuizResponses(componentProgressId, {
        responses,
        classProductId,
    })

    cb.onLog('메타인지 완료', 'success')
}

// ============================================================================
// Process Single Component
// ============================================================================

async function processComponent(
    lessonId: number,
    component: ComponentDetail,
    classProductId: number,
    cb: AutoCompleteCallbacks,
): Promise<void> {
    const componentLocation = toComponentLocation(component.type, component.location)
    const label = `${component.type}(${component.location})`

    if (component.progress?.status === 'completed') {
        cb.onLog(`${label} - 이미 완료됨, 스킵`, 'info')
        return
    }

    cb.onLog(`${label} 시작...`, 'info')

    const session = await learningSessionApi.startSession({
        lessonId,
        componentType: component.type,
        componentLocation,
        classProductId,
    })

    switch (component.type) {
        case 'video':
            await autoCompleteVideo(
                session.componentProgressId,
                session.videoData?.duration || 300,
                classProductId,
                cb,
            )
            break

        case 'quiz':
            if (session.quizData && session.sessionId != null) {
                await autoCompleteQuiz(
                    session.sessionId,
                    session.componentProgressId,
                    session.quizData.quizzes,
                    classProductId,
                    cb,
                )
            } else {
                cb.onLog('퀴즈 데이터가 없습니다', 'warn')
            }
            break

        case 'assignment':
            if (session.diaryQuizData) {
                await autoCompleteDiary(
                    session.componentProgressId,
                    session.diaryQuizData.quizzes,
                    classProductId,
                    cb,
                )
            } else {
                cb.onLog('다이어리 데이터가 없습니다', 'warn')
            }
            break
    }
}

// ============================================================================
// Public API: Auto-Complete Single Lesson
// ============================================================================

export async function autoCompleteLesson(
    lessonId: number,
    params: ClassCourseParams,
    cb: AutoCompleteCallbacks,
): Promise<void> {
    cb.onLog(`레슨 ${lessonId} 자동 완료 시작`, 'info')

    const details = await learningSessionApi.getLessonDetails({
        lessonId,
        classProductId: params.classProductId,
        productId: params.productId,
        classId: params.classId,
    })

    if (details.overallStatus === 'completed') {
        cb.onLog(`레슨 "${details.bareTitle || details.title}" 이미 완료됨, 스킵`, 'success')
        return
    }

    const enabledComponents = details.components.filter(c => c.enabled)
    cb.onLog(`레슨: ${details.bareTitle || details.title} (활성 컴포넌트: ${enabledComponents.length}개)`, 'info')

    for (let i = 0; i < enabledComponents.length; i++) {
        checkAbort(cb.signal)
        cb.onProgress(i, enabledComponents.length, `컴포넌트 ${i + 1}/${enabledComponents.length}`)
        await processComponent(lessonId, enabledComponents[i], params.classProductId, cb)

        if (i < enabledComponents.length - 1) {
            await sleep(DELAY_BETWEEN_COMPONENTS_MS)
        }
    }

    // Wait for progress propagation
    await sleep(DELAY_BETWEEN_COMPONENTS_MS)

    // Verify lesson completion
    const verify = await learningSessionApi.getLessonDetails({
        lessonId,
        classProductId: params.classProductId,
        productId: params.productId,
        classId: params.classId,
    })

    if (verify.overallStatus === 'completed') {
        cb.onLog(`레슨 완료 검증 OK`, 'success')
    } else {
        cb.onLog(`레슨 미완료 감지 (status: ${verify.overallStatus}) - reconciliation 대기 중...`, 'warn')
        await sleep(1000)
        const recheck = await learningSessionApi.getLessonDetails({
            lessonId,
            classProductId: params.classProductId,
            productId: params.productId,
            classId: params.classId,
        })
        if (recheck.overallStatus === 'completed') {
            cb.onLog(`레슨 완료 (reconciliation 보정됨)`, 'success')
        } else {
            cb.onLog(`레슨 여전히 미완료: ${recheck.overallStatus}`, 'error')
        }
    }

    cb.onProgress(enabledComponents.length, enabledComponents.length, '완료')
}

// ============================================================================
// Public API: Auto-Complete Module Lessons (현재 모듈의 레슨만)
// ============================================================================

export async function autoCompleteModuleLessons(
    resolvedContentId: number,
    moduleId: number,
    params: ClassCourseParams,
    cb: AutoCompleteCallbacks,
): Promise<void> {
    cb.onLog(`모듈 ${moduleId}의 레슨 일괄 완료 시작`, 'info')

    const hierarchy = await contentApi.getContentHierarchy(resolvedContentId)

    // Find the specific module and collect its lessons
    function findModuleLessons(node: ContentHierarchyNode): { id: number; title: string }[] {
        if (node.type === 'module' && node.id === moduleId) {
            return collectLessons(node)
        }
        return node.children.flatMap(findModuleLessons)
    }

    const lessons = hierarchy.children.flatMap(findModuleLessons)

    if (lessons.length === 0) {
        cb.onLog(`모듈 ${moduleId}에 레슨이 없습니다`, 'warn')
        cb.onComplete()
        return
    }

    cb.onLog(`모듈 레슨: ${lessons.length}개`, 'info')

    // Get completed lesson IDs to skip already-completed ones
    const completedIds = await learningSessionApi.getCompletedLessonIds(resolvedContentId, params.classProductId)
    const completedSet = new Set(completedIds)
    const pendingLessons = lessons.filter(l => !completedSet.has(l.id))

    cb.onLog(`미완료 레슨: ${pendingLessons.length}개 (이미 완료: ${lessons.length - pendingLessons.length}개)`, 'info')

    if (pendingLessons.length === 0) {
        cb.onLog('모든 레슨이 이미 완료되었습니다', 'success')
        cb.onComplete()
        return
    }

    for (let i = 0; i < pendingLessons.length; i++) {
        checkAbort(cb.signal)
        const lesson = pendingLessons[i]
        cb.onProgress(i, pendingLessons.length, `레슨 ${i + 1}/${pendingLessons.length}: ${lesson.title}`)
        cb.onLog(`\n--- 레슨 ${i + 1}/${pendingLessons.length}: ${lesson.title} ---`, 'info')

        try {
            await autoCompleteLesson(lesson.id, params, cb)
        } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') throw err
            cb.onLog(`레슨 실패: ${lesson.title} - ${err instanceof Error ? err.message : err}`, 'error')
        }

        if (i < pendingLessons.length - 1) {
            await sleep(DELAY_BETWEEN_LESSONS_MS)
        }
    }

    cb.onProgress(pendingLessons.length, pendingLessons.length, '전체 완료')
    cb.onComplete()
}

// ============================================================================
// Public API: Auto-Complete Module End Quiz
// ============================================================================

export async function autoCompleteModuleEndQuiz(
    moduleId: number,
    classProductId: number,
    cb: AutoCompleteCallbacks,
    options?: { leaveLast?: number },
): Promise<void> {
    const leaveLast = options?.leaveLast ?? 0
    cb.onLog(`모듈 ${moduleId} 마무리 퀴즈 자동 완료 시작${leaveLast > 0 ? ` (마지막 ${leaveLast}문제 남김)` : ''}`, 'info')

    // Check activity status
    const statuses = await learningSessionApi.getModuleActivityStatuses([moduleId], classProductId)
    const status = statuses.find(s => s.moduleId === moduleId)

    if (status) {
        if (!status.endActivityEnabled) {
            cb.onLog('마무리 퀴즈가 비활성화되어 있습니다', 'warn')
            cb.onComplete()
            return
        }
        if (status.endActivityCompleted) {
            cb.onLog('이미 완료된 마무리 퀴즈입니다', 'success')
            cb.onComplete()
            return
        }
    }

    // Start module activity session
    cb.onLog('모듈 마무리 퀴즈 세션 시작', 'info')
    const session = await learningSessionApi.startModuleActivity({
        moduleId,
        activityType: 'end',
        classProductId,
        questionCount: 10,
        shuffleQuestions: true,
    })

    if (!session.quizData || session.sessionId == null) {
        cb.onLog('퀴즈 데이터가 없습니다', 'warn')
        cb.onComplete()
        return
    }

    const quizzes = session.quizData.quizzes
    const solveCount = leaveLast > 0
        ? Math.max(0, quizzes.length - leaveLast)
        : quizzes.length

    cb.onLog(`세션 시작 (문제 수: ${quizzes.length}${leaveLast > 0 ? `, ${solveCount}문제만 풀기` : ''})`, 'info')

    // Solve each quiz (up to solveCount)
    let correctCount = 0
    for (let i = 0; i < solveCount; i++) {
        checkAbort(cb.signal)
        cb.onProgress(i, solveCount, `문제 ${i + 1}/${quizzes.length}`)
        const quiz = quizzes[i]

        const quizDetail = await quizScriptApi.getQuiz(quiz.quizId)
        const vars = (quizDetail?.vars as Record<string, unknown>) || {}
        const basePayload = { quizId: quiz.quizId, scriptId: quiz.scriptId, vars, timeSpentSec: 3 }

        // 1st: dummy answer
        const firstResult = await learningSessionApi.submitQuizResponse(session.sessionId, {
            ...basePayload,
            answers: getDummyAnswers(quiz.type),
        })

        if (firstResult.isCorrect) {
            cb.onLog(`  문제 ${i + 1}/${quizzes.length}: 정답 (${quiz.type})`, 'info')
            correctCount++
            continue
        }

        if (!firstResult.correctAnswers || firstResult.correctAnswers.length === 0) {
            cb.onLog(`  문제 ${i + 1}/${quizzes.length}: correctAnswers 없음 (${quiz.type})`, 'warn')
            continue
        }

        // 2nd: correct answer
        const secondResult = await learningSessionApi.submitQuizResponse(session.sessionId, {
            ...basePayload,
            answers: firstResult.correctAnswers,
        })

        cb.onLog(
            `  문제 ${i + 1}/${quizzes.length}: ${secondResult.isCorrect ? '정답' : '오답'} (${quiz.type})`,
            secondResult.isCorrect ? 'info' : 'warn',
        )
        if (secondResult.isCorrect) correctCount++
    }

    // leaveLast mode: don't complete session
    if (leaveLast > 0) {
        cb.onLog(`마지막 ${leaveLast}문제 남김 - 세션 완료 호출 안 함`, 'warn')
        cb.onLog(`${solveCount}/${quizzes.length} 문제 제출 완료 (${correctCount}개 정답). 나머지는 직접 풀어주세요!`, 'success')
        cb.onProgress(solveCount, solveCount, '완료 (일부 남김)')
        cb.onComplete()
        return
    }

    // Complete session
    const completion = await learningSessionApi.completeSession(session.componentProgressId, {
        componentType: 'quiz',
        forceComplete: false,
        classProductId,
        passScore: 0,
    })

    cb.onLog(`마무리 퀴즈 완료! (${correctCount}/${quizzes.length} 정답, 점수: ${completion.score ?? '-'}점)`, 'success')
    cb.onProgress(quizzes.length, quizzes.length, '완료')
    cb.onComplete()
}
